import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useProject } from "../../contexts/ProjectContext";
import { BrandMark } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { ChangePasswordDialog } from "../../components/ChangePasswordDialog";
import { HealthCheckDialog } from "../../components/HealthCheckDialog";
import { DataResetDialog } from "../../components/DataResetDialog";
import { WhatsAppSupport } from "../../components/WhatsAppSupport";
import {
  LayoutDashboard,
  HelpCircle,
  Image,
  Users,
  Users2,
  Store,
  Package,
  FileSpreadsheet,
  Briefcase,
  ClipboardList,
  LogOut,
  Settings,
  KeyRound,
  Activity,
  Trash2,
} from "lucide-react";

const tabs = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, key: "overview" },
  { to: "/admin/projects", label: "Projects", icon: Briefcase, key: "projects" },
  { to: "/admin/clients", label: "Clients", icon: Users2, key: "clients" },
  { to: "/admin/promoters", label: "Promoters", icon: Users, key: "promoters" },
  { to: "/admin/stores", label: "Stores", icon: Store, key: "stores" },
  { to: "/admin/skus", label: "SKUs", icon: Package, key: "skus" },
  { to: "/admin/survey-options", label: "Survey options", icon: ClipboardList, key: "survey-options" },
  { to: "/admin/faq", label: "FAQ", icon: HelpCircle, key: "faq" },
  { to: "/admin/login-page", label: "Login page", icon: Image, key: "login-page" },
  { to: "/admin/reports", label: "Reports", icon: FileSpreadsheet, key: "reports" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { projects, projectId, setProjectId } = useProject();
  const loc = useLocation();
  const current = tabs.find((t) => (t.end ? loc.pathname === t.to : loc.pathname.startsWith(t.to)));
  const [pwdOpen, setPwdOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass-bar">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-2.5 sm:py-0 sm:h-16 flex flex-wrap items-center gap-y-2">
          <div className="order-1">
            <BrandMark compact />
          </div>
          <div className="order-2 sm:order-3 ml-auto sm:ml-0 flex items-center gap-2 sm:gap-3">
            <div className="hidden md:block text-right leading-tight">
              <div className="text-xs font-medium" data-testid="admin-username">
                {user?.name}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                Administrator
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-slate-300/70 h-9"
                  data-testid="admin-settings-trigger"
                  aria-label="Account settings"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-xl border-slate-300/70 w-56"
                data-testid="admin-settings-menu"
              >
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  Account
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => setPwdOpen(true)}
                  className="rounded-xl cursor-pointer"
                  data-testid="open-change-password"
                >
                  <KeyRound className="h-4 w-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  System
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => setHealthOpen(true)}
                  className="rounded-xl cursor-pointer"
                  data-testid="open-health-check"
                >
                  <Activity className="h-4 w-4 mr-2" /> Database health
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-rose-700">
                  Danger zone
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => setResetOpen(true)}
                  className="rounded-xl cursor-pointer text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                  data-testid="open-data-reset"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Reset data…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="rounded-xl border-slate-300/70 h-9"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          <div className="order-3 sm:order-2 w-full sm:w-auto sm:ml-auto sm:mr-3">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger
                className="rounded-full h-9 w-full sm:w-[260px] font-medium"
                data-testid="admin-project-switcher"
              >
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All projects</SelectItem>
                {projects
                  .filter((p) => (p.status || "active") === "active" || p.id === projectId)
                  .map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`project-switcher-${p.id}`}>
                    <span className="font-medium">{p.name}</span>
                    <span className="ml-2 font-mono text-[10px] text-neutral-500">
                      {p.start_date} → {p.end_date}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <nav className="border-t border-white/50">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-8 flex overflow-x-auto gap-1 py-2">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.key}
                  to={t.to}
                  end={t.end}
                  data-testid={`admin-nav-${t.key}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm rounded-full transition-colors whitespace-nowrap ${
                      isActive
                        ? "bg-slate-900 text-white font-semibold shadow-md"
                        : "text-slate-500 hover:text-slate-900 hover:bg-white/70"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-5 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
            Admin / {current?.label || "—"}
          </div>
          <h1 className="font-display font-semibold text-2xl sm:text-4xl tracking-tight mt-1">
            {current?.label || "Admin"}
          </h1>
        </div>
        <Outlet />
        <div className="mt-10 flex justify-center">
          <WhatsAppSupport />
        </div>
      </main>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <HealthCheckDialog open={healthOpen} onOpenChange={setHealthOpen} />
      <DataResetDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        onDone={() => {
          // Hard reload so all admin lists re-fetch fresh
          window.location.reload();
        }}
      />
    </div>
  );
}
